/**
 * Roles del equipo del proyecto.
 *
 * **Single source of truth**: el catálogo de templates (`template_type='role'`).
 * Los componentes del frontend hacen `useQuery(['templates','list','role'])`,
 * filtran por el `applicable_scopes` que viene en el payload, y poblan los
 * combos con el `template_key` como value y `template.name` como label.
 *
 * **Internal vs Client**: el filtro se hace por `applicable_scopes` en la
 * `definition` del template (Approval Workflow Fase 0 — migración 0027):
 *
 *   - Internal members (PM, BA, Dev, QA, ...): roles con
 *     `applicable_scopes` que incluya `'internal'`.
 *   - Client stakeholders (Sponsor, Reviewer, Auditor, Final user):
 *     roles con `applicable_scopes` que incluya `'client_stakeholder'`.
 *
 * Templates legacy (sin `applicable_scopes` declarado, raros) se asumen
 * como `'internal'` para compatibilidad — defensa para no romper
 * tenants con clones antiguos que aún no fueron migrados.
 */

import type { RoleScope, Template } from '../../../api/templates'

/** A qué lado de la mesa aplica un role template. Es información del
 * catálogo (`applicable_scopes`) y sobrevive a OT-0008: dice para qué
 * sirve el rol, no qué permisos otorga. */
export type { RoleScope }

/** Lo que los selectores de rol necesitan. Lo cumplen el `Template` del
 * catálogo (wizard) y el `ProjectRole` de `GET /projects/{id}/roles`
 * (Equipo & RACI, OT-0058). */
export type RoleOption = Pick<Template, 'template_key' | 'name' | 'applicable_scopes'>

/** Filtra un listado de role templates por scope aplicable.
 *
 * Reglas:
 *   - Si `applicable_scopes` incluye el scope buscado → incluye.
 *   - Si `applicable_scopes` es `null`/`undefined`/`[]` → solo incluye
 *     cuando el scope buscado es `'internal'` (default legacy seguro).
 *
 * El sort es por `name.localeCompare` para presentación estable.
 */
export function filterRolesByScope<T extends RoleOption>(
  templates: T[],
  scope: RoleScope,
  locale: string = 'es',
): T[] {
  return templates
    .filter((t) => {
      const scopes = t.applicable_scopes
      if (Array.isArray(scopes) && scopes.length > 0) {
        return scopes.includes(scope)
      }
      // Legacy fallback: roles sin applicable_scopes declarado se
      // asumen como internal. Cuando todos los tenants migren al seed
      // nuevo, se puede endurecer a `return false`.
      return scope === 'internal'
    })
    .sort((a, b) => a.name.localeCompare(b.name, locale))
}

/** True si `role` (un `template_key`) corresponde a un role del catálogo
 * con scope `client_stakeholder` declarado. Usado por la tabla del
 * Team para decidir si renderizar dropdown editable de stakeholders.
 *
 * Acepta lookup contra una lista cargada del catálogo. Si no la
 * encuentra (template fue archivado, p. ej.), devuelve `false` y la UI
 * cae al modo "free text" mostrando el role tal cual está guardado.
 */
export function isClientStakeholderRole(
  role: string,
  templates: RoleOption[],
): boolean {
  const tpl = templates.find((t) => t.template_key === role)
  if (!tpl) return false
  const scopes = tpl.applicable_scopes
  return (
    Array.isArray(scopes) && scopes.includes('client_stakeholder')
  )
}

/** Devuelve el `template_key` del primer role internal del catálogo.
 *
 * OT-0008 D8 — antes priorizaba el role marcado `is_project_lead`. Ese
 * flag ya no existe: el liderazgo es de una persona concreta
 * (`projects.lead_member_id`), no de una etiqueta del catálogo. Lo que
 * queda es un default razonable para el formulario de alta.
 *
 * Si el catálogo internal está vacío, devuelve `null`.
 */
export function defaultInternalRoleKey(
  templates: RoleOption[],
  locale: string = 'es',
): string | null {
  const internalRoles = filterRolesByScope(templates, 'internal', locale)
  return internalRoles[0]?.template_key ?? null
}

/** Nombre visible de un rol, o su clave si el catálogo ya no lo conoce. */
export function roleLabel(key: string, roles: RoleOption[]): string {
  return roles.find((r) => r.template_key === key)?.name ?? key
}

/** Validación de email simple para stakeholders externos. */
export function isValidEmail(value: string): boolean {
  if (!value) return false
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}
